import type { ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import type { z } from "zod";

/**
 * Modül sistemi — iki katman:
 *
 * - **Blueprint (motor)**: yeniden kullanılabilir route/nav/section şablonu.
 *   `params` alır; aynı blueprint farklı parametrelerle birden fazla kez
 *   çalışabilir (ör. `commerce` → fashion ve technology).
 * - **Instance (modül)**: kullanıcıya görünen modül. Bir blueprint seçer,
 *   `basePath`, `subdomain`, tema, isim ve `params` verir.
 *
 * Parametre URL query string'inde taşınmaz; instance path prefix'inden
 * (`/fashion/...`) ya da subdomain'den (`shop.chatharmony.ai`) çözülür.
 * API çağrılarına ise blueprint hook'ları tarafından query string olarak eklenir.
 */

export type BlueprintId = "commerce" | "models";

export type ModuleId =
  | "fashion"
  | "technology"
  | "models"
  | "deep-search"
  | "social"
  | "business"
  | "entertainment";

export type ModuleStatus = "active" | "soon";

/** Route'un shell (sidebar + header) içinde mi, tam ekran mı açılacağı. */
export type ModuleLayout = "shell" | "bare";

/** Sidebar ve modül kartları için ikon tipi — `harmony-icons-inline` ve lucide ile uyumlu. */
export type ModuleIcon = ComponentType<{ size?: number; className?: string }>;

/**
 * Route'un oturum gereksinimi — router builder buna göre guard sarmalar:
 *   private  → PrivateRoute (oturum şart)
 *   optional → OptionalAuthRoute (guest görebilir, oturumlu için gate akışı)
 *   public   → guard yok
 *   guest    → PublicRoute (yalnızca oturumsuz; login/register sayfaları)
 * Tanımsızsa `"private"`.
 */
export type ModuleRouteAuth = "private" | "optional" | "public" | "guest";

/**
 * Route `handle` alanı. Blueprint route'ları yalnızca `layout` ve `auth`
 * yazar; `moduleId` router builder tarafından modül kök route'una eklenir ve
 * `useMatches()` bunu okuyup aktif modülü çözer.
 */
export interface ModuleRouteHandle {
  moduleId?: ModuleId;
  layout?: ModuleLayout;
  auth?: ModuleRouteAuth;
}

export const isModuleRouteHandle = (
  handle: unknown,
): handle is ModuleRouteHandle & { moduleId: ModuleId } =>
  typeof handle === "object" &&
  handle !== null &&
  typeof (handle as ModuleRouteHandle).moduleId === "string";

/** Blueprint route'larında `handle` yazımını kısaltır ve tipler. */
export const routeHandle = (
  handle: Omit<ModuleRouteHandle, "moduleId">,
): ModuleRouteHandle => handle;

/** Sidebar navigasyon öğesi — `to` basePath'e göredir (baş / olmadan). */
export interface ModuleNavItem {
  id: string;
  labelKey: string;
  icon: ModuleIcon;
  /** basePath'e göre segment. Boş string modülün kök sayfası. */
  to: string;
}

/**
 * Header slot'ları — Faz 2'de doldurulur. Tip şimdi tanımlanıyor ki
 * blueprint'ler ileride kırılmadan slot ekleyebilsin.
 */
export interface ModuleHeaderSlots {
  left?: ComponentType[];
  right?: ComponentType[];
}

export interface ModuleChatConfig {
  enabled: boolean;
  /** Modüle özel agent kimliği; tanımsızsa genel asistan. */
  agentId?: string;
}

/** Blueprint'in route/nav üretirken eline geçen bağlam. */
export interface BlueprintContext<P> {
  instance: ModuleInstance<P>;
  params: P;
}

export interface ModuleBlueprint<P> {
  id: BlueprintId;
  /** Instance `params`'ı registry oluşurken bu şemayla doğrulanır. */
  paramsSchema: z.ZodType<P>;
  /**
   * basePath'e GÖRE route'lar (baş / olmadan). Router builder bunları
   * `path: basePath, handle: { moduleId }` altına yerleştirir. Tam ekran
   * sayfa için route'a `handle: { layout: "bare" }` eklenir.
   */
  routes: (ctx: BlueprintContext<P>) => RouteObject[];
  nav: (ctx: BlueprintContext<P>) => ModuleNavItem[];
  headerSlots?: ModuleHeaderSlots;
  chat: ModuleChatConfig;
}

/** Instance'ın blueprint'e ekleme yapabildiği noktalar. */
export interface ModuleExtensions {
  /** Slot adı → o slota eklenecek section bileşenleri. */
  sections?: Record<string, ComponentType[]>;
  /** Blueprint route'larına ek, instance'a özel route'lar (basePath'e göre). */
  routes?: RouteObject[];
}

export interface ModuleTheme {
  /** OKLCH hue (0–360). `--primary` ve türevleri bundan üretilir. */
  hue: number;
}

export interface ModuleInstance<P = unknown> {
  id: ModuleId;
  /**
   * Blueprint'siz instance yalnızca `status: "soon"` olabilir: sidebar'da
   * "yakında" kartı görünür, route/nav üretilmez.
   */
  blueprint?: BlueprintId;
  /** Ana domainde mount edileceği prefix, ör. `/fashion`. */
  basePath: `/${string}`;
  /** Eşleşen subdomain'de instance root'a (`""`) mount edilir, ör. `shop`. */
  subdomain?: string;
  nameKey: string;
  /** Hub kartındaki tek cümlelik açıklama (i18n anahtarı). */
  descriptionKey: string;
  icon: ModuleIcon;
  status: ModuleStatus;
  theme: ModuleTheme;
  /** Blueprint yoksa `undefined`. */
  params?: P;
  extensions?: ModuleExtensions;
}

/** `commerce` blueprint parametreleri. */
export interface CommerceParams {
  /** API listelemelerine `vertical=` query string'i olarak gider. */
  vertical: "fashion" | "technology" | (string & {});
  features?: {
    /** Gardırop / try-on akışları — teknoloji için anlamsız, kapatılır. */
    wardrobe?: boolean;
  };
}

/** `models` blueprint parametresizdir. */
export type ModelsParams = Record<string, never>;
